import TagManager from "@sooro-io/react-gtm-module";
import type { Order, Product, ProductVariant } from "@shopify/hydrogen-react/storefront-api-types";

export const APP_NAME = import.meta.env.VITE_REACT_APP_NAME as string;

const GTM_KEY = {
  tune: "GTM-MKXPWZ2T",
  yeseyesee: localStorage.getItem("i18nextLng") === "ko" ? "GTM-TXM3GKTJ" : "GTM-PCGT6G5N",
  thisisneverthat: localStorage.getItem("i18nextLng") === "ko" ? "GTM-P7WWMKDB" : "GTM-W2XJNPD8",
  khakis: localStorage.getItem("i18nextLng") === "ko" ? "GTM-M4QMGQ6Q" : "GTM-PG22DBLF",
};

export default function useTracking() {
  const gtmInit = () => {
    if (GTM_KEY[APP_NAME as keyof typeof GTM_KEY] !== "") {
      TagManager.initialize({
        gtmId: GTM_KEY[APP_NAME as keyof typeof GTM_KEY],
      });
    }
  };

  const gtmViewItem = (product: Product) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "view_item",
        item_id: product.id.replace("gid://shopify/Product/", ""),
        item_name: product.title,
        item_brand: product.vendor,
        item_category: product.productType,
        price: product.priceRange.maxVariantPrice.amount,
        compare_at_price: product.compareAtPriceRange.maxVariantPrice.amount,
        available: product.availableForSale,
        image: product.featuredImage?.url,
        currency: product.priceRange.maxVariantPrice.currencyCode,
      },
    });
  };

  const gtmAddToCart = (variant: ProductVariant) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "add_to_cart",
        ecommerce: {
          items: [
            {
              item_variant_id: variant.id.replace("gid://shopify/ProductVariant/", ""),
              item_id: variant.sku,
              item_name: variant.product.title,
              item_brand: variant.product.vendor,
              item_variant_name: variant.title,
              item_category: variant.product.productType,
              quantity: 1,
              price: Number(variant.price.amount),
              compare_at_price: Number(variant.compareAtPrice?.amount),
              available: variant.availableForSale,
              image: variant.image?.url,
            },
          ],
          value: Number(variant.price.amount),
          currency: variant.price.currencyCode,
        },
      },
    });
  };

  const gtmAddToWishList = (variant: ProductVariant) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "add_to_wishlist",
        ecommerce: {
          items: [
            {
              item_variant_id: variant.id.replace("gid://shopify/ProductVariant/", ""),
              item_name: variant.product.title,
              quantity: 1,
            },
          ],
          value: Number(variant.price.amount),
          currency: variant.price.currencyCode,
        },
      },
    });
  };

  const gtmPurchase = (order: Order) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "purchase",
        ecommerce: {
          order_id: order.name,
          order_total_amount: Number(order.totalPrice.amount),
          order_total_quantity: order.lineItems.edges.length,
          currency: order.totalPrice.currencyCode,
          order_items: order.lineItems.edges.map(({ node }, index) => {
            return {
              index,
              item_variant_id: node.variant?.id.replace("gid://shopify/ProductVariant/", ""),
              item_id: node.variant?.sku,
              item_name: node.variant?.product.title,
              item_brand: node.variant?.product.vendor,
              item_variant_name: node.variant?.title,
              item_category: node.variant?.product.productType,
              quantity: Number(node.quantity),
              price: Number(node.discountedTotalPrice.amount) / Number(node.quantity),
            };
          }),
        },
      },
    });
  };

  const gtmLocationChanged = (location: { pathname: string }) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "location_changed",
        page: location.pathname,
      },
    });
  };

  return {
    gtmInit,
    gtmAddToCart,
    gtmAddToWishList,
    gtmViewItem,
    gtmPurchase,
    gtmLocationChanged,
  };
}
